import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';

export function LoginPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const passwordReset = (location.state as { passwordReset?: boolean } | null)?.passwordReset ?? false;

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const init     = useAuthStore((s) => s.init);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await signIn({ username: email, password });
      if (!result.isSignedIn) {
        setError(t('auth.sign_in_failed'));
        return;
      }
      await init();
      const { isAuthenticated, role } = useAuthStore.getState();
      if (!isAuthenticated) {
        setError(t('auth.sign_in_failed'));
        return;
      }
      navigate(role === 'student' ? '/student' : '/parent', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('auth.sign_in_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <div className="app-content" style={{ backgroundColor: 'white', justifyContent: 'center' }}>

          {/* Logo */}
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)', margin: 0 }}>
              VADOStudy
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '4px', marginBottom: 0 }}>
              {t('auth.tagline')}
            </p>
            <div style={{ marginTop: '12px' }}>
              <LanguageSwitcher variant="compact" aria-label={t('auth.language_label')} />
            </div>
          </div>

          {/* Password-reset success banner */}
          {passwordReset && (
            <p style={{
              color: 'var(--success)', fontSize: '0.85rem', margin: '0 0 16px',
              padding: '10px 14px', background: '#F0FDF4', borderRadius: '8px', lineHeight: 1.5,
            }}>
              ✅ {t('auth.success_banner')}
            </p>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
          >
            <div className="form-group">
              <label htmlFor="email">{t('auth.email_label')}</label>
              <input
                id="email"
                type="email"
                placeholder={t('auth.email_placeholder')}
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            <PasswordInput
              id="password"
              label={t('auth.password_label')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
            />

            <Link
              to="/forgot-password"
              className="link-secondary"
              style={{ fontSize: '0.85rem', alignSelf: 'flex-end' }}
            >
              {t('auth.forgot_password')}
            </Link>

            {error && (
              <p style={{
                color: 'var(--danger)',
                fontSize: '0.85rem',
                margin: 0,
                padding: '10px 14px',
                background: '#FEF2F2',
                borderRadius: '8px',
                lineHeight: 1.5,
              }}>
                ⚠️ {error}
                <br />
                <Link to="/forgot-password" className="link-secondary" style={{ fontSize: '0.85rem' }}>
                  {t('auth.forgot_password')}
                </Link>
              </p>
            )}

            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? t('auth.signing_in') : t('auth.sign_in_btn')}
            </button>
          </form>

          {/* Divider */}
          <hr className="divider" />

          {/* Navigation links */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <Link to="/register" className="link-secondary">
              {t('auth.no_account')} <strong>{t('auth.create_one')}</strong>
            </Link>
            <Link to="/child-login" className="link-secondary">
              {t('auth.have_join_code')} <strong>{t('auth.sign_in_here')}</strong>
            </Link>
            <Link to="/register-student" className="link-secondary">
              {t('auth.university_prompt')} <strong>{t('auth.create_account_btn')}</strong>
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
}
